import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Save, Check } from 'lucide-react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/core/auth/AuthContext';
import { useTeamNew } from './hooks/useTeamNew';
import { BasicInfoStep } from './components/BasicInfoStep';
import { MembersStep } from './components/MembersStep';
import type { TeamNewFormValues } from './types/teamNew.types';
import { cn } from '@/utils/cn';

const STEPS = [
  { id: 1, label: '1. Thông tin cơ bản' },
  { id: 2, label: '2. Thành viên' },
  { id: 3, label: '3. Phân quyền' },
  { id: 4, label: '4. Xác nhận' },
];

export default function TeamsNewPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const { referenceData, isLoading, isSubmitting, createTeam } = useTeamNew(companyId);

  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState<TeamNewFormValues>({
    name: '',
    code: '',
    service_category: 'construction',
    team_type: 'Thi công',
    base_capacity: 8,
    status_code: 'active',
    founded_date: new Date().toISOString().split('T')[0],
    members: [],
  });

  const handleUpdateForm = (patch: Partial<TeamNewFormValues>) => {
    setForm(prev => ({ ...prev, ...patch }));
  };

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!companyId) {
      console.warn('[TeamsNewPage] Cannot submit: companyId is missing');
      return;
    }
    if (!form.name) {
      toast.error('Vui lòng nhập tên team');
      return;
    }
    try {
      await createTeam(form);
      navigate('/teams');
    } catch (err: any) {
      console.error('Failed to create team', err);
    }
  };

  return (
    <PageLayout
      title="Thêm team"
      description="Tạo mới team để quản lý và theo dõi công việc hiệu quả"
      headerActions={
        <Button variant="outline" onClick={() => navigate('/teams')} className="h-9">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Quay lại
        </Button>
      }
    >
      <div className="max-w-[1600px] mx-auto space-y-8 pb-20">
        {/* Stepper Header */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          {STEPS.map((step) => (
            <div 
              key={step.id}
              className={cn(
                "flex-1 text-center py-2 border-b-2 transition-all relative",
                currentStep === step.id ? "border-emerald-600 text-emerald-600" : "border-transparent text-slate-400"
              )}
            >
              <span className="sm:text-sm text-xs font-normal tracking-wider">{step.label}</span>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          {currentStep === 1 && (
            <BasicInfoStep 
              form={form} 
              onChange={handleUpdateForm} 
              referenceData={referenceData} 
            />
          )}
          
          {currentStep === 2 && (
            <MembersStep 
              form={form} 
              onChange={handleUpdateForm} 
              referenceData={referenceData} 
            />
          )}
          
          {currentStep > 2 && (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-md border border-dashed border-slate-200">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                <Check className="w-8 h-8" />
              </div>
              <h3 className="sm:text-sm text-xs font-normal text-slate-900">Tính năng đang được phát triển</h3>
              <p className="sm:text-sm text-xs text-slate-400 mt-1">Cảm ơn bạn đã chờ đợi.</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-white/80 backdrop-blur-md border-t border-slate-100 px-8 py-4 z-20">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <Button 
              variant="outline" 
              className="sm:text-sm text-xs h-10 px-8 border-slate-200 text-slate-600 hover:bg-slate-50"
              onClick={currentStep === 1 ? () => navigate('/teams') : handlePrev}
            >
              {currentStep === 1 ? 'Hủy bỏ' : 'Quay lại'}
            </Button>
            
            <div className="flex items-center gap-3">
              {currentStep === 1 && (
                <Button 
                  variant="outline"
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 sm:text-sm text-xs h-10 px-6"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  disabled={isLoading || !companyId}
                >
                  Lưu & Hoàn tất
                </Button>
              )}

              {currentStep < 4 ? (
                <Button 
                  className="bg-emerald-800 hover:bg-emerald-900 text-white sm:text-sm text-xs h-10 px-10 -emerald-900/10 border-none disabled:opacity-50"
                  onClick={handleNext}
                  disabled={isLoading || !companyId}
                >
                  Tiếp tục
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  className="bg-emerald-800 hover:bg-emerald-900 text-white sm:text-sm text-xs h-10 px-10 -emerald-900/10 border-none"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  disabled={isLoading || !companyId}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Hoàn tất
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
